import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import { DeviceProfileService } from '../../../../services/device-profile.service';
import { DeviceService } from '../../../../services/device.service';
import { DiagramService } from '../../../../services/diagram.service';
import { ButtonIconComponent } from '../../../../shared/components/button-icon/button-icon.component';
import { ChartMixedComponent } from '../../../../shared/components/charts/chart-mixed/chart-mixed.component';
import {
  createWaterMeasurementChartData,
  createWaterMeasurementChartOptions,
} from '../../../../shared/components/charts/measurement-chart.config';
import {
  TimeRangeSelectorComponent,
} from '../../../../shared/components/time-range-selector/time-range-selector.component';
import { AccountRole } from '../../../../types/dtos/account.dto';
import { DeviceDto, DeviceMeasurementDto, MeasurementResponseDto } from '../../../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../../../types/dtos/deviceProfile.dto';
import { ReportType } from '../../../../types/enums/report.enum';
import {
  MixedChartDataSetsInterface,
  MixedChartDateRangeInterface,
  MixedChartInterface,
  MixedChartOptionsInterface,
  TimeRangeKey,
} from '../../../../types/interfaces/charts.interface';
import { DashboardBoxComponent } from '../../../dashboard/dashboard-box/dashboard-box.component';
import {TranslateSafePipe} from '../../../../shared/pipes/translation.pipe';

@Component({
  selector: 'app-device-detail-consumption',
  imports: [
    ButtonIconComponent,
    ChartMixedComponent,
    DashboardBoxComponent,
    TimeRangeSelectorComponent,
    TranslateSafePipe,
  ],
  templateUrl: './device-detail-consumption.html',
  styleUrl: './device-detail-consumption.css',
})
export class DeviceDetailConsumption implements OnInit {
  public waterMeasurementData: MixedChartInterface;
  public waterMeasurementOptions: MixedChartOptionsInterface = createWaterMeasurementChartOptions();
  protected readonly AccountRole = AccountRole;
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);
  private _dateFrom: Date | null = null;
  private _dateTo: Date | null = null;
  private _type: TimeRangeKey = '1W';
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly _deviceService: DeviceService,
    private readonly _deviceProfileService: DeviceProfileService,
    private readonly _diagramService: DiagramService,
    private readonly _router: Router,
    private readonly _translateService: TranslateService,
    private readonly _authService: AuthService,
  ) {
    this.waterMeasurementData = createWaterMeasurementChartData(this._translateService);
  }

  private _device: DeviceDto | null = null;

  @Input()
  set device(value: DeviceDto | null) {
    this._device = value;

    if (!this._device) return;

    this.clearWaterMeasurementData();

    const from: Date = new Date();
    from.setDate(from.getDate() - 7);
    this.requestMeasurement({
      type: this._type,
      to: this._dateTo ?? new Date(),
      from: this._dateFrom ?? from,
    });
  }

  public ngOnInit(): void {
    this._translateService.onLangChange
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event: LangChangeEvent) => {
        let dataset: MixedChartDataSetsInterface[] = createWaterMeasurementChartData(this._translateService).datasets;

        for (let i = 0; i < dataset.length; i++) {
          this.waterMeasurementData.datasets[i].label = dataset[i].label;
        }
        this.waterMeasurementData = {
          ...this.waterMeasurementData,
        };
      });
  }

  public goToReports(): void {
    let type: ReportType = ReportType.DEVICE_DETAIL
    let device_eui: string | undefined = this._device?.device_eui;
    if (!this._dateFrom || !this._dateTo) return
    let from: number = Math.floor(this._dateFrom.getTime() / 1000);
    let to: number = Math.floor(this._dateTo.getTime() / 1000);

    this._router.navigate(['/reports'], { queryParams: { type, device_eui, from, to } })
      .then();
  }

  public requestMeasurement(range: MixedChartDateRangeInterface): void {
    if (!this._device) {
      return;
    }

    const now: Date = new Date();
    const safeTo: Date = range.to > now
      ? now
      : range.to;

    this.waterMeasurementData = {
      ...this.waterMeasurementData,
      loading: true,
    };

    this._type = range.type;
    this._dateFrom = range.from;
    this._dateTo = range.to;

    let measurementName: string = 'METER_VALUE_1';

    if (this._device.device_profile_id) {
      let deviceProfile: DeviceProfileDto | null = this._deviceProfileService.getDeviceProfileById(this._device.device_profile_id);

      if (deviceProfile?.primary_value) {
        measurementName = deviceProfile.primary_value;
      }
    }

    this._deviceService
      .getDeviceMeasurements(this._device.id, {
        measurement: measurementName,
        fromDate: Math.floor(range.from.getTime() / 1000),
        toDate: Math.floor(safeTo.getTime() / 1000),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: MeasurementResponseDto) => {
          if (!result.data || result.data.length === 0) {
            this.clearWaterMeasurementData();

            return;
          }

          this.setMixedChartData(
            result.data as DeviceMeasurementDto[],
            range.from,
            safeTo,
            range.type,
          );
        },
        error: (error: unknown) => {
          this.waterMeasurementData = {
            ...this.waterMeasurementData,
            loading: false,
          };

          console.error('Error fetching device measurements:', error);
        },
      });
  }

  public hasAccess(role: AccountRole): boolean {
    return this._authService.canAccess(role);
  }

  private setMixedChartData(
    data: DeviceMeasurementDto[],
    fromDate: Date,
    toDate: Date,
    type: TimeRangeKey,
  ): void {
    const timeSeriesData: {
      labels: string[],
      data: number[]
    } = this._diagramService.generateTimeSeriesData(
      new Date(fromDate),
      new Date(toDate),
      type,
      data,
    );

    this.waterMeasurementData = {
      loading: false,
      labels: timeSeriesData.labels,
      datasets: [
        {
          ...this.waterMeasurementData.datasets[0],
          data: timeSeriesData.data,
        },
        {
          ...this.waterMeasurementData.datasets[1],
          data: this._diagramService.calcDeltaValues(timeSeriesData.data), // Delta
        },
      ],
    };

    this._deviceService.waterMeasurementData = this.waterMeasurementData;
  }

  private clearWaterMeasurementData(): void {
    this.waterMeasurementData = {
      ...this.waterMeasurementData,
      loading: false,
      labels: [],
      datasets: this.waterMeasurementData.datasets.map(dataset => ({
        ...dataset,
        data: [],
      })),
    };
  }
}
